/**
 * Générateur — Automatisme : relations multiplicatives simples
 * Attendu ID : 1037 | Niveau : 6ème | Domaine : NP
 * Rubrique : AUTOMATISME (programme 2025) — « L'élève sait repérer des
 * relations multiplicatives simples entre des nombres (double, quadruple,
 * moitié, tiers, quart). »
 * Labels (cartes exerciseur) :
 *   - Double, quadruple, moitié, tiers, quart
 *
 * ── Plan de cartes (2026-08-22, cadrage prof) ──────────────────────────────
 * CINQ relations, et cinq seulement : double, quadruple, moitié, tiers,
 * quart — PAS de triple (l'asymétrie est dans le texte du programme).
 * Le cœur : REPÉRER la relation entre deux nombres (« 10 est ... de 30 »),
 * en précisant « relation multiplicative » par la grille de mots elle-même.
 * La moitié des IMPAIRS ≤ 15 (~20 % des moitiés : le fait CM2, réponse 3,5).
 * Théorème d'unicité : une paire (a, b) ne peut correspondre qu'à UNE des
 * cinq relations (× et partage sont disjoints, et 2/3/4 ne se confondent
 * pas) — les autres mots sont donc toujours faux par construction.
 * HORS PÉRIMÈTRE : le triple, « n fois plus/moins » (→ attendu voisin),
 * les tableaux de proportionnalité, tout symbole ÷.
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeSaisie, makeVraiFaux,
} from '../../../utils';

const ATTENDU_ID = 1037;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L1 = 'Double, quadruple, moitié, tiers, quart';

const garde = (condition, message) => {
    if (!condition) throw new Error(`gen_1037 : ${message}`);
};

/** Les CINQ relations. `type` : 'fois' (n × k) ou 'part' (n partagé en k). */
const RELATIONS = [
    { id: 'double', mot: 'le double', k: 2, type: 'fois' },
    { id: 'quadruple', mot: 'le quadruple', k: 4, type: 'fois' },
    { id: 'moitie', mot: 'la moitié', k: 2, type: 'part' },
    { id: 'tiers', mot: 'le tiers', k: 3, type: 'part' },
    { id: 'quart', mot: 'le quart', k: 4, type: 'part' },
];

const fmtFr = (x) => (Number.isInteger(x) ? String(x) : String(x).replace('.', ','));

/** Base adaptée à la relation — hiérarchie réelle du CM2 (cadrage prof
 *  2026-08-22) : double/quadruple sans contrainte ; moitié libre sur les
 *  pairs, impairs ≤ 15 seulement (la borne du programme) ; QUART = la
 *  moitié de la moitié (multiples de 4 ≤ 100, en privilégiant ceux dont
 *  les deux moitiés successives restent rondes) ; TIERS dans les tables
 *  (≤ 36 : aucun critère de divisibilité par 3 n'est au programme, l'élève
 *  reconnaît par les tables). */
const QUARTS_RONDS = [20, 24, 40, 48, 60, 80, 100];
const tirerBase = (rel, entierSeulement = false) => {
    switch (rel.id) {
        case 'double': return randInt(3, 50);
        case 'quadruple': return randInt(3, 25);
        case 'moitie':
            if (!entierSeulement && Math.random() < 0.2) return randomChoice([3, 5, 7, 9, 11, 13, 15]);
            return 2 * randInt(2, 50);
        case 'tiers': return 3 * randInt(2, 12);
        default: return Math.random() < 0.7 ? randomChoice(QUARTS_RONDS) : 4 * randInt(2, 25);
    }
};

const appliquer = (rel, n) => (rel.type === 'fois' ? n * rel.k : n / rel.k);

const question = (rel, n) => (rel.id === 'moitie'
    ? `Quelle est la moitié de ${n} ?`
    : `Quel est ${rel.mot} de ${n} ?`);

/** Distracteurs nommés : inverse (l'erreur cible), voisine, addition —
 *  puis un vivier ±1/±k (jamais de bouche-trou inventé hors nombre). */
const distracteursCalcul = (rel, n, bonne) => {
    const candidats = [];
    // la relation INVERSE : l'erreur cible (la moitié quand on double...) —
    // TOUJOURS présente : arrondie à l'entier si elle tombe sur un décimal
    // (le quart de 17 → 4,25 → 4 ; demande prof 2026-08-22)
    let inverse = rel.type === 'fois' ? n / rel.k : n * rel.k;
    if (!Number.isInteger(inverse * 2)) inverse = Math.round(inverse);
    candidats.push({ valeur: inverse, code: 'relation_inverse' });
    // la relation VOISINE (2 ↔ 4, 3 ↔ 2)
    const voisinK = rel.k === 2 ? 4 : 2;
    candidats.push({
        valeur: rel.type === 'fois' ? n * voisinK : n / voisinK,
        code: 'relation_voisine',
    });
    // l'ADDITION au lieu de la multiplication
    candidats.push({ valeur: rel.type === 'fois' ? n + rel.k : n - rel.k, code: 'addition' });
    // recopie
    candidats.push({ valeur: n, code: 'recopie' });
    // vivier
    [bonne + 1, bonne - 1, bonne + rel.k, bonne - rel.k].forEach((v) => {
        candidats.push({ valeur: v, code: 'vivier' });
    });
    const vus = new Set([fmtFr(bonne)]);
    return candidats
        .filter((c) => c.valeur > 0 && (Number.isInteger(c.valeur * 2)))
        .map((c) => ({ ...c, valeur: fmtFr(c.valeur) }))
        .filter((c) => {
            if (vus.has(c.valeur)) return false;
            vus.add(c.valeur);
            return true;
        })
        .slice(0, 3);
};

// ─── Format 1 : QCM calcul ────────────────────────────────────────────────

const finirQcm = (bonne, dis, texte, sousType) => {
    const payload = makeQcm(
        [bonne, ...dis.map((c) => c.valeur)],
        bonne,
        texte,
        opts(L1),
        false,
    );
    payload.contenu = { ...payload.contenu, sousType };
    payload._meta.params = { sousType, distracteurs: dis };
    return payload;
};

/** Le SENS du mot (demande prof 2026-08-22) : « prendre le triple, c'est
 *  multiplier par 3 » — la mécompréhension testée frontalement. LE TRIPLE
 *  est admis dans CE sous-type seulement (il ne teste que le sens du mot,
 *  pas le fait calculatoire, qui reste hors périmètre). */
const SENS = [
    { mot: 'le double', k: 2, type: 'fois' },
    { mot: 'le triple', k: 3, type: 'fois' },
    { mot: 'le quadruple', k: 4, type: 'fois' },
    { mot: 'la moitié', k: 2, type: 'part' },
    { mot: 'le tiers', k: 3, type: 'part' },
    { mot: 'le quart', k: 4, type: 'part' },
];

const genSensQcm = () => {
    const r = randomChoice(SENS);
    const bonne = r.type === 'fois' ? `multiplier par ${r.k}` : `partager en ${r.k}`;
    const dis = [
        // l'inverse : LA mécompréhension
        { valeur: r.type === 'fois' ? `partager en ${r.k}` : `multiplier par ${r.k}`, code: 'operation_inverse' },
        { valeur: `ajouter ${r.k}`, code: 'addition' },
        { valeur: `soustraire ${r.k}`, code: 'soustraction' },
    ];
    return finirQcm(bonne, dis, `<p><b>Prendre ${r.mot} d'un nombre, c'est...</b></p>`, 'sens');
};

const genRelationsQcm = () => {
    if (Math.random() < 0.25) return genSensQcm();
    const rel = randomChoice(RELATIONS);
    const n = tirerBase(rel);
    const bonne = appliquer(rel, n);
    garde(Number.isInteger(bonne * 2), `résultat non demi-entier : ${rel.id} de ${n}`);
    const dis = distracteursCalcul(rel, n, bonne);
    return finirQcm(fmtFr(bonne), dis, `<p><b>${question(rel, n)}</b></p>`, rel.id);
};

// ─── Format 2 : saisie ────────────────────────────────────────────────────

const genRelationsSaisie = () => {
    const rel = randomChoice(RELATIONS);
    const n = tirerBase(rel);
    const bonne = appliquer(rel, n);
    garde(Number.isInteger(bonne * 2), `résultat non demi-entier : ${rel.id} de ${n}`);
    const payload = makeSaisie(fmtFr(bonne), `<p><b>${question(rel, n)}</b></p>`, opts(L1));
    payload._meta.params = { sousType: `saisie_${rel.id}` };
    return payload;
};

// ─── Format 3 : REPÉRER la relation (le cœur de l'attendu) ────────────────

const genRepererQcm = () => {
    const rel = randomChoice(RELATIONS);
    const n = tirerBase(rel, true);                       // entiers seulement
    const b = appliquer(rel, n);
    garde(Number.isInteger(b), `repérer : b non entier (${rel.id} de ${n})`);
    // les 4 autres mots sont FAUX par construction (théorème d'unicité)
    const dis = shuffle(RELATIONS.filter((r) => r.id !== rel.id))
        .slice(0, 3)
        .map((r) => ({ valeur: r.mot, code: 'autre_relation' }));
    return finirQcm(rel.mot, dis,
        `<p><b>Complète : ${fmtFr(b)} est ... de ${n}.</b></p>`, 'reperer');
};

// ─── Format 4 : V/F (lignes générées, vérité par construction) ────────────

const genRelationsVraiFaux = () => {
    for (let essai = 0; essai < 40; essai++) {
        const lignes = [];
        const verites = [];
        const vues = new Set();
        const rels = shuffle([...RELATIONS, randomChoice(RELATIONS)]).slice(0, 4);
        for (const rel of rels) {
            const n = tirerBase(rel);
            const vrai = appliquer(rel, n);
            const estVraie = Math.random() < 0.5;
            let valeur = vrai;
            if (!estVraie) {
                // le faux vient d'une relation NOMMÉE (voisine ou inverse)
                const faux = shuffle([
                    rel.type === 'fois' ? n / rel.k : n * rel.k,
                    rel.type === 'fois' ? n * (rel.k === 2 ? 4 : 2) : n / (rel.k === 2 ? 4 : 2),
                ]).find((v) => v > 0 && Number.isInteger(v * 2) && v !== vrai);
                if (faux === undefined) continue;
                valeur = faux;
            }
            const ligne = `${fmtFr(valeur)} est ${rel.mot} de ${n}`;
            if (vues.has(ligne)) continue;
            vues.add(ligne);
            lignes.push(ligne);
            // vérité RECALCULÉE depuis l'affiché, jamais l'intention
            verites.push(Math.abs(valeur - vrai) < 1e-9);
        }
        if (lignes.length !== 4) continue;
        const nbVraies = verites.filter(Boolean).length;
        if (nbVraies < 1 || nbVraies > 3) continue;
        return makeVraiFaux(
            lignes,
            verites.map((v) => (v ? 0 : 1)),
            '<p><b>Vrai ou faux ?</b></p>',
            opts(L1),
        );
    }
    return makeVraiFaux(
        ['18 est le double de 9', '12 est le quart de 40', '3,5 est la moitié de 7', '10 est le tiers de 30'],
        [0, 1, 0, 0],
        '<p><b>Vrai ou faux ?</b></p>',
        opts(L1),
    );
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'CINQ relations : double, quadruple, moitié, tiers, quart — jamais le triple',
        'moitié des impairs ≤ 15 (~20 % des moitiés) : le fait CM2, réponse en x,5',
        'TIERS dans les tables (≤ 36) ; QUART = multiples de 4 ≤ 100, moitiés successives rondes privilégiées (jamais de 4,5)',
        'hors périmètre : « n fois plus/moins » (attendu voisin), tableaux, symbole ÷',
        'repérer : les 4 autres mots sont faux PAR CONSTRUCTION (une paire = une seule relation)',
    ],
    relations_qcm_unique: {
        fixes: {
            relation_inverse: 'la réciproque (la moitié quand on demandait le double) — L\'ERREUR CIBLE',
            relation_voisine: '2 ↔ 4 (le quadruple au lieu du double, le quart au lieu de la moitié)',
            addition: 'n + k ou n − k : additionner au lieu de multiplier',
            recopie: 'le nombre recopié',
        },
        vivier: '±1 / ±k autour de la bonne réponse, en complément seulement',
        sens: '« prendre le triple, c\'est... » → {multiplier par k ; partager en k (l\'inverse) ; ajouter k ; soustraire k} — le triple admis ICI seulement (sens du mot)',
    },
    relations_vf: {
        garde: 'les lignes fausses viennent d\'une relation NOMMÉE (voisine ou inverse), vérité recalculée depuis l\'affiché',
    },
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    relations_qcm_unique: genRelationsQcm,
    relations_saisie: genRelationsSaisie,
    reperer_qcm_unique: genRepererQcm,
    relations_vf: genRelationsVraiFaux,
};

const FORMAT_LABELS = {
    relations_qcm_unique: 'QCM calcul',
    relations_saisie: 'Saisie',
    reperer_qcm_unique: 'Repérer',
    relations_vf: 'Vrai/Faux',
};

export const CATEGORIES = [
    {
        id: 'relationsmult',
        label: L1,
        formatsList: Object.keys(GENERATORS).map((k) => ({ key: k, label: FORMAT_LABELS[k] || k })),
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
